import { formatKennelStatus } from "@/lib/badge-styles";

export interface AnimalAlerts {
  struggling: boolean;
  worsening: boolean;
  streak: boolean;
  spike: boolean;
  highSeverity: boolean;
  flagged: boolean;
}

export interface AnimalExportRow {
  id: string;
  animalId: string;
  name: string;
  species: string;
  location: string;
  zoneCode: string;
  kennelStatusNorm?: string | null;
  substatusNorm?: string | null;
  kennelStatusSince?: string | null;
  kennelStatusSinceIsApprox?: boolean;
  adoptionLevelNorm?: string | null;
  lastSeenDate: string;
  intakeDate?: string | null;
  hasMedicalNotes: boolean;
  isOutcomed: boolean;
  evaluation: {
    status: string | null;
    label: string | null;
  };
  alerts?: AnimalAlerts | null;
}

export interface ExportField<T> {
  header: string;
  getValue: (row: T) => string;
}

export interface ExportProfile<T = AnimalExportRow> {
  id: string;
  label: string;
  fields: ExportField<T>[];
}

function serializeAlerts(alerts: AnimalAlerts | null | undefined): string {
  if (!alerts) return "";
  const active: string[] = [];
  if (alerts.struggling) active.push("Struggling");
  if (alerts.worsening) active.push("Worsening");
  if (alerts.streak) active.push("3+ Day Streak");
  if (alerts.spike) active.push("Rate Spike");
  if (alerts.highSeverity) active.push("High Severity");
  if (alerts.flagged) active.push("Flagged");
  return active.join(", ");
}

function daysInStatus(since: string | null | undefined): number | null {
  if (!since) return null;
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(since);
  const d = new Date(isDateOnly ? since + "T00:00:00" : since);
  if (isNaN(d.getTime())) return null;
  return Math.max(0, Math.round((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24)));
}

function formatStatusCell(row: AnimalExportRow): string {
  if (row.isOutcomed) return "Outcomed";
  return formatKennelStatus(row.kennelStatusNorm, row.substatusNorm);
}

const ALL_TABLE_FIELDS_PROFILE: ExportProfile = {
  id: "all-table-fields",
  label: "All table fields",
  fields: [
    { header: "Name", getValue: (r) => r.name ?? "" },
    { header: "AID", getValue: (r) => r.animalId ?? "" },
    { header: "Species", getValue: (r) => r.species ?? "" },
    { header: "Location", getValue: (r) => r.location ?? "" },
    { header: "Kennel", getValue: (r) => r.zoneCode ?? "" },
    { header: "Status", getValue: (r) => formatStatusCell(r) },
    { header: "Days in Status", getValue: (r) => {
      if (r.isOutcomed) return "";
      const days = daysInStatus(r.kennelStatusSince);
      if (days === null) return "";
      return `${r.kennelStatusSinceIsApprox ? "~" : ""}${days}`;
    }},
    { header: "Adoption Level", getValue: (r) => r.adoptionLevelNorm ?? "" },
    { header: "Evaluation", getValue: (r) => r.evaluation?.label ?? r.evaluation?.status ?? "" },
    { header: "Signals", getValue: (r) => serializeAlerts(r.alerts) },
    { header: "Intake Date", getValue: (r) => r.intakeDate ?? "" },
    { header: "Last Seen", getValue: (r) => r.lastSeenDate ?? "" },
  ],
};

export const ANIMAL_EXPORT_PROFILES: ExportProfile[] = [
  ALL_TABLE_FIELDS_PROFILE,
];

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function formatAnimalsAsHtml(rows: AnimalExportRow[], profile: ExportProfile = ALL_TABLE_FIELDS_PROFILE): string {
  const headerCells = profile.fields
    .map((f) => `<th style="padding:4px 8px;text-align:left;border-bottom:2px solid #ccc;white-space:nowrap">${escapeHtml(f.header)}</th>`)
    .join("");
  const bodyRows = rows.map((row) => {
    const cells = profile.fields
      .map((f) => `<td style="padding:4px 8px;border-bottom:1px solid #eee;white-space:nowrap">${escapeHtml(f.getValue(row))}</td>`)
      .join("");
    return `<tr>${cells}</tr>`;
  }).join("");
  return `<table style="border-collapse:collapse;font-family:monospace;font-size:13px"><thead><tr>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>`;
}

function escapeCell(value: string, delimiter: string): string {
  if (delimiter === "\t") return value.replace(/\t/g, " ").replace(/\n/g, " ");
  if (value.includes('"') || value.includes(",") || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function buildRows(rows: AnimalExportRow[], profile: ExportProfile, delimiter: string): string {
  const header = profile.fields.map((f) => escapeCell(f.header, delimiter)).join(delimiter);
  const body = rows.map((row) =>
    profile.fields.map((f) => escapeCell(f.getValue(row), delimiter)).join(delimiter)
  );
  return [header, ...body].join("\n");
}

export function formatAnimalsAsTsv(rows: AnimalExportRow[], profile: ExportProfile = ALL_TABLE_FIELDS_PROFILE): string {
  return buildRows(rows, profile, "\t");
}

export function formatAnimalsAsCsv(rows: AnimalExportRow[], profile: ExportProfile = ALL_TABLE_FIELDS_PROFILE): string {
  return buildRows(rows, profile, ",");
}

export function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
